<template>
  <v-dialog persistent width="640" v-model="active">
    <v-card class="ui-module-card ui-module-fade-in-up" color="primary">
      <v-card-title class="font-weight-bold justify-center ui-module-titlebar">
        {{ esEdicion ? 'Editar asignación' : 'Nueva asignación de equipo' }}
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12" sm="6" v-if="!esEdicion">
              <v-select
                class="ui-form-field"
                v-model="form.idSubSede"
                filled
                rounded
                dense
                color="secondary"
                background-color="transparent"
                item-color="secondary"
                :items="subsedes"
                item-value="idSubSede"
                item-text="subSede"
                :rules="required"
              >
                <template v-slot:label>Subsede <span class="red--text">*</span></template>
              </v-select>
            </v-col>

            <v-col cols="12" :sm="esEdicion ? 6 : 6">
              <v-menu
                v-model="menuFechaSolicitud"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    class="ui-form-field"
                    :value="form.fechaSolicitud"
                    filled
                    rounded
                    dense
                    readonly
                    color="secondary"
                    background-color="transparent"
                    append-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    :rules="required"
                  >
                    <template v-slot:label>Fecha de solicitud <span class="red--text">*</span></template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="form.fechaSolicitud"
                  locale="es"
                  color="secondary"
                  no-title
                  @input="menuFechaSolicitud = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6">
              <v-menu
                v-model="menuFechaAsignacion"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    class="ui-form-field"
                    :value="form.fechaAsignacion"
                    filled
                    rounded
                    dense
                    readonly
                    color="secondary"
                    background-color="transparent"
                    append-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    :rules="required"
                  >
                    <template v-slot:label>Fecha de asignación <span class="red--text">*</span></template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="form.fechaAsignacion"
                  locale="es"
                  color="secondary"
                  no-title
                  @input="menuFechaAsignacion = false"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                class="ui-form-field"
                v-model="form.folioResguardo"
                filled
                rounded
                dense
                color="secondary"
                background-color="transparent"
                label="Folio de resguardo"
                autocomplete="off"
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-file-input
                class="ui-form-field"
                v-model="form.adjunto"
                filled
                rounded
                dense
                color="secondary"
                background-color="transparent"
                label="Adjunto (opcional)"
              ></v-file-input>
            </v-col>

            <v-col cols="12">
              <v-autocomplete
                class="ui-form-field"
                v-model="equiposSeleccionados"
                filled
                rounded
                dense
                color="secondary"
                background-color="transparent"
                item-color="secondary"
                :items="equiposDisponibles"
                item-text="nombre"
                item-value="idEquipamiento"
                multiple
                chips
                small-chips
                deletable-chips
                return-object
                hide-selected
                :rules="requiredEquipos"
              >
                <template v-slot:label>Equipos <span class="red--text">*</span></template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12">
              <v-textarea
                class="ui-form-field"
                v-model="form.observaciones"
                filled
                rounded
                dense
                color="secondary"
                background-color="transparent"
                rows="4"
                label="Observaciones"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions class="justify-center pb-4 ui-module-actions">
        <v-btn text class="ui-module-btn-text-danger mx-8" @click="closeDialog()">Cancelar</v-btn>
        <v-btn
          color="secondary"
          class="ui-module-btn-solid mx-8"
          :loading="loading"
          :disabled="!isFormValid || loading"
          @click="save()"
          >Guardar</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { ESTATUS_EQUIPO_ACTIVO } from '@/const'

export default {
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    subsedes: {
      type: Array,
      default: () => [],
    },
    asignacion: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      menuFechaSolicitud: false,
      menuFechaAsignacion: false,
      equiposSeleccionados: [],
      form: {
        idSubSede: null,
        fechaSolicitud: '',
        fechaAsignacion: '',
        folioResguardo: '',
        observaciones: '',
        adjunto: null,
      },
      required: [(v) => !!v || 'Este campo es requerido'],
    }
  },

  computed: {
    ...mapGetters('equipo', { equipos: 'getEquipos' }),

    esEdicion() {
      return !!this.asignacion
    },

    requiredEquipos() {
      return [() => this.equiposSeleccionados.length > 0 || 'Selecciona al menos un equipo']
    },

    equipoIdsSeleccionadosActualmente() {
      return new Set(this.equiposSeleccionados.map((e) => e.idEquipamiento))
    },

    equiposDisponibles() {
      const activos = (this.equipos || [])
        .filter((e) => e.CatEstatus && e.CatEstatus.id === ESTATUS_EQUIPO_ACTIVO)
        .map((e) => ({
          idEquipamiento: e.idEquipamiento,
          nombre: `${e.CatItem?.item || ''} ${e.numero ? '- ' + e.numero : ''}`.trim(),
        }))

      const yaSeleccionadosNoActivos = this.equiposSeleccionados.filter(
        (e) => !activos.some((a) => a.idEquipamiento === e.idEquipamiento)
      )

      return [...activos, ...yaSeleccionadosNoActivos]
    },

    isFormValid() {
      return (
        !!this.form.fechaSolicitud &&
        !!this.form.fechaAsignacion &&
        this.equiposSeleccionados.length > 0 &&
        (this.esEdicion || !!this.form.idSubSede)
      )
    },
  },

  watch: {
    asignacion: {
      immediate: true,
      handler(val) {
        if (val) {
          this.form.idSubSede = val.idSubSede
          this.form.fechaSolicitud = val.fechaSolicitud ? val.fechaSolicitud.substring(0, 10) : ''
          this.form.fechaAsignacion = val.fechaAsignacion ? val.fechaAsignacion.substring(0, 10) : ''
          this.form.folioResguardo = val.folioResguardo || ''
          this.form.observaciones = val.observaciones || ''
          this.form.adjunto = null
          this.equiposSeleccionados = (val.equipoAsignado || []).map((e) => ({
            idEquipamiento: e.fk_idEquipo,
            nombre: `${e.equipo || ''} ${e.numero ? '- ' + e.numero : ''}`.trim(),
            idEquipoAsignacion: e.id,
          }))
        } else {
          this.resetForm()
        }
      },
    },
  },

  methods: {
    ...mapActions('equipo', ['fetchEquipo']),
    ...mapActions('subsedeAsignacion', ['createAsignacionEquipo', 'updateAsignacionEquipo']),

    resetForm() {
      this.form = {
        idSubSede: null,
        fechaSolicitud: '',
        fechaAsignacion: '',
        folioResguardo: '',
        observaciones: '',
        adjunto: null,
      }
      this.equiposSeleccionados = []
      this.loading = false
    },

    closeDialog() {
      this.resetForm()
      this.$emit('close')
    },

    async save() {
      this.loading = true
      try {
        if (this.esEdicion) {
          const idsSeleccionados = new Set(this.equiposSeleccionados.map((e) => e.idEquipamiento))

          const equipoAsignadoExistente = (this.asignacion.equipoAsignado || []).map((e) => ({
            id: e.id,
            fk_idEquipo: e.fk_idEquipo,
            estatus: idsSeleccionados.has(e.fk_idEquipo),
          }))

          const equipoAsignadoNuevo = this.equiposSeleccionados
            .filter((e) => !e.idEquipoAsignacion)
            .map((e) => ({ fk_idEquipo: e.idEquipamiento }))

          await this.updateAsignacionEquipo({
            idSubSede: this.asignacion.idSubSede,
            idDetalleAsignacion: this.asignacion.idDetalleAsignacion,
            payload: {
              fechaSolicitud: this.form.fechaSolicitud,
              fechaAsignacion: this.form.fechaAsignacion,
              observaciones: this.form.observaciones,
              folioResguardo: this.form.folioResguardo,
              equipoAsignado: [...equipoAsignadoExistente, ...equipoAsignadoNuevo],
              adjunto: this.form.adjunto,
            },
          })
        } else {
          const equipoAsignadoNuevo = this.equiposSeleccionados.map((e) => ({ fk_idEquipo: e.idEquipamiento }))

          await this.createAsignacionEquipo({
            idSubSede: this.form.idSubSede,
            payload: {
              fechaSolicitud: this.form.fechaSolicitud,
              fechaAsignacion: this.form.fechaAsignacion,
              observaciones: this.form.observaciones,
              folioResguardo: this.form.folioResguardo,
              equipoAsignado: equipoAsignadoNuevo,
              adjunto: this.form.adjunto,
            },
          })
        }

        this.closeDialog()
      } catch {
        // error ya manejado en el store (snackbar)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
